import React, { useState } from 'react'
import { motion } from 'framer-motion'
import {
  PlayIcon,
  VideoCameraIcon,
  UserGroupIcon,
  HeartIcon,
  ChatBubbleLeftIcon,
  GiftIcon,
  EyeIcon,
  SignalIcon,
  SparklesIcon,
  FireIcon
} from '@heroicons/react/24/outline'

const LivePage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'live' | 'upcoming' | 'replay'>('live')

  const liveStreams = [
    {
      id: 1,
      streamer: 'Sarah Miller',
      title: 'Late Night Chat & Q&A',
      viewers: 1234,
      thumbnail: '',
      duration: '2h 15m',
      isLive: true,
      category: 'Entertainment'
    },
    {
      id: 2,
      streamer: 'Mike Johnson',
      title: 'Cooking Together',
      viewers: 856,
      thumbnail: '',
      duration: '45m',
      isLive: true,
      category: 'Lifestyle'
    },
    {
      id: 3,
      streamer: 'Emma Davis',
      title: 'Fitness & Wellness',
      viewers: 2145,
      thumbnail: '',
      duration: '1h 30m',
      isLive: true,
      category: 'Health'
    },
    {
      id: 4,
      streamer: 'Alex Chen',
      title: 'Music Session',
      viewers: 567,
      thumbnail: '',
      duration: '55m',
      isLive: true,
      category: 'Music'
    }
  ]

  return (
    <div className="space-y-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between"
      >
        <div>
          <div className="flex items-center space-x-3 mb-2">
            <VideoCameraIcon className="h-10 w-10 text-primary-400" />
            <h1 className="text-4xl font-display font-bold">
              Live <span className="gradient-text">Streaming</span>
            </h1>
          </div>
          <p className="text-white/70">Watch live streams and connect with creators</p>
        </div>
        
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="btn-primary flex items-center space-x-2"
        >
          <VideoCameraIcon className="h-5 w-5" />
          <span>Go Live</span>
        </motion.button>
      </motion.div>

      {/* Featured Live Stream */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="card-glass overflow-hidden"
      >
        <div className="relative h-96 bg-gradient-to-br from-primary-500/20 to-secondary-500/20 flex items-center justify-center">
          <PlayIcon className="h-24 w-24 text-white/30" />
          
          {/* Live Badge */}
          <div className="absolute top-4 left-4 flex items-center space-x-2 px-4 py-2 bg-red-500 rounded-full">
            <SignalIcon className="h-4 w-4 text-white animate-pulse" />
            <span className="font-bold text-sm">LIVE</span>
          </div>

          {/* Viewer Count */}
          <div className="absolute top-4 right-4 flex items-center space-x-2 px-4 py-2 bg-black/50 backdrop-blur-md rounded-full">
            <EyeIcon className="h-4 w-4" />
            <span className="font-medium">3.2K watching</span>
          </div>

          {/* Stream Info */}
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-2xl font-bold mb-2">Weekend Vibes & Chill</h2>
                <div className="flex items-center space-x-4 text-sm text-white/70">
                  <div className="flex items-center space-x-2">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-r from-primary-600 to-secondary-600" />
                    <span>Jessica Anderson</span>
                  </div>
                  <span>•</span>
                  <span>Entertainment</span>
                  <span>•</span>
                  <span>2h 45m</span>
                </div>
              </div>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="btn-primary"
              >
                Watch Now
              </motion.button>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Stats */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="grid grid-cols-2 md:grid-cols-4 gap-6"
      >
        <StatsCard icon={VideoCameraIcon} label="Live Now" value="48" color="red" />
        <StatsCard icon={UserGroupIcon} label="Total Viewers" value="12.5K" color="blue" />
        <StatsCard icon={FireIcon} label="Trending" value="24" color="orange" />
        <StatsCard icon={SparklesIcon} label="Premium" value="16" color="purple" />
      </motion.div>

      {/* Tabs */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="flex space-x-2 border-b border-white/10"
      >
        {(['live', 'upcoming', 'replay'] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-6 py-3 font-medium transition-all capitalize relative ${
              activeTab === tab
                ? 'text-white'
                : 'text-white/70 hover:text-white'
            }`}
          >
            {tab}
            {activeTab === tab && (
              <motion.div
                layoutId="activeTab"
                className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-primary-600 to-secondary-600"
              />
            )}
          </button>
        ))}
      </motion.div>

      {/* Live Streams Grid */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
      >
        {liveStreams.map((stream, index) => (
          <LiveStreamCard key={stream.id} stream={stream} index={index} />
        ))}
      </motion.div>

      {/* Categories */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="space-y-4"
      >
        <h2 className="text-2xl font-bold">Browse by Category</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <CategoryCard name="Entertainment" count={24} />
          <CategoryCard name="Music" count={12} />
          <CategoryCard name="Gaming" count={18} />
          <CategoryCard name="Lifestyle" count={15} />
          <CategoryCard name="Health" count={8} />
          <CategoryCard name="Education" count={10} />
        </div>
      </motion.div>
    </div>
  )
}

interface StatsCardProps {
  icon: React.ComponentType<any>
  label: string
  value: string
  color: 'red' | 'blue' | 'orange' | 'purple'
}

const StatsCard: React.FC<StatsCardProps> = ({ icon: Icon, label, value, color }) => {
  const colorClasses = {
    red: 'from-red-600/20 to-red-400/20 text-red-400',
    blue: 'from-blue-600/20 to-blue-400/20 text-blue-400',
    orange: 'from-orange-600/20 to-orange-400/20 text-orange-400',
    purple: 'from-purple-600/20 to-purple-400/20 text-purple-400'
  }

  return (
    <motion.div
      whileHover={{ scale: 1.05 }}
      className={`card-glass p-6 bg-gradient-to-br ${colorClasses[color]}`}
    >
      <Icon className="h-8 w-8 mb-4" />
      <p className="text-2xl font-bold text-white">{value}</p>
      <p className="text-sm text-white/70">{label}</p>
    </motion.div>
  )
}

interface LiveStreamCardProps {
  stream: any
  index: number
}

const LiveStreamCard: React.FC<LiveStreamCardProps> = ({ stream, index }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.9 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ delay: index * 0.1 }}
    whileHover={{ scale: 1.03, y: -5 }}
    className="card-glass overflow-hidden cursor-pointer group"
  >
    <div className="relative h-48 bg-gradient-to-br from-primary-500/20 to-secondary-500/20 flex items-center justify-center">
      <PlayIcon className="h-12 w-12 text-white/30 group-hover:scale-125 transition-transform" />
      
      {/* Live Badge */}
      {stream.isLive && (
        <div className="absolute top-3 left-3 flex items-center space-x-1 px-2 py-1 bg-red-500 rounded-md">
          <SignalIcon className="h-3 w-3 text-white animate-pulse" />
          <span className="font-bold text-xs">LIVE</span>
        </div>
      )}

      {/* Viewer Count */}
      <div className="absolute top-3 right-3 flex items-center space-x-1 px-2 py-1 bg-black/50 backdrop-blur-md rounded-md">
        <EyeIcon className="h-3 w-3" />
        <span className="text-xs font-medium">{stream.viewers}</span>
      </div>

      {/* Duration */}
      <div className="absolute bottom-3 right-3 px-2 py-1 bg-black/50 backdrop-blur-md rounded-md">
        <span className="text-xs font-medium">{stream.duration}</span>
      </div>
    </div>

    <div className="p-4">
      <div className="flex items-start space-x-3 mb-3">
        <div className="w-10 h-10 rounded-full bg-gradient-to-r from-primary-600 to-secondary-600 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <h3 className="font-bold truncate">{stream.title}</h3>
          <p className="text-sm text-white/70 truncate">{stream.streamer}</p>
        </div>
      </div>

      <div className="flex items-center justify-between text-sm text-white/50">
        <span className="px-2 py-1 bg-white/10 rounded-md">{stream.category}</span>
        <div className="flex items-center space-x-2">
          <HeartIcon className="h-4 w-4" />
          <ChatBubbleLeftIcon className="h-4 w-4" />
          <GiftIcon className="h-4 w-4" />
        </div>
      </div>
    </div>
  </motion.div>
)

interface CategoryCardProps {
  name: string
  count: number
}

const CategoryCard: React.FC<CategoryCardProps> = ({ name, count }) => (
  <motion.div
    whileHover={{ scale: 1.05 }}
    className="card-glass p-4 cursor-pointer text-center"
  >
    <p className="font-bold mb-1">{name}</p>
    <p className="text-sm text-white/70">{count} live</p>
  </motion.div>
)

export default LivePage
